import { MarketDataUnresolvedError } from "./marketDataResolution";
import type { OpenInstrumentSetupOptions } from "../contexts/InstrumentSetupContext";
import { toastError } from "./showApiErrorToast";

export type HandleMarketDataUnresolvedOptions = {
  /** When true, opens instrument setup (Trade Review full gate only). Default false — inline nudge. */
  openSetupOnError?: boolean;
};

/**
 * Handles 422 market-data resolution errors. By default shows a toast only;
 * callers (report wizard, rerun dialog, sim) should render inline connect/setup nudges.
 */
export function handleMarketDataUnresolvedError(
  error: unknown,
  openSetup: (accountCode: string, options?: OpenInstrumentSetupOptions) => void | Promise<void>,
  brokerAccountCode: string | null | undefined,
  options?: HandleMarketDataUnresolvedOptions
): boolean {
  if (!(error instanceof MarketDataUnresolvedError)) return false;

  const detail = error.body;
  const preferenceUnavailable =
    detail.error === "market_data_preference_unavailable" ||
    detail.chart_unavailable_reason === "market_data_preference_unavailable";

  if (preferenceUnavailable) {
    toastError(detail.message || "Your saved chart data source is not connected.", {
      description: detail.action ?? "Reconnect your broker or switch OHLC source.",
    });
  } else {
    toastError(error.message || "Instrument market data setup is required.", {
      description: detail.action,
    });
  }

  if (options?.openSetupOnError) {
    const code = brokerAccountCode?.trim();
    const symbol = detail.symbol?.trim();
    if (code) {
      void openSetup(code, {
        mandatory: !preferenceUnavailable,
        focusSymbol: symbol || undefined,
      });
    }
  }

  return true;
}
